﻿import * as React from "react";
import { Dialog, DialogTitleWithCloseButton, DialogContent, DialogActions, LinkButton, Button, DatePicker, TextField } from "../../../shared/components";
import type { DHTMLXGanttItem } from "./types";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

interface EditTaskProps {
    task: DHTMLXGanttItem;
    showDialog: boolean;
    setShowDialog: (showDialog: boolean) => void;
    onTaskEdited: (task: DHTMLXGanttItem) => void;
    onBeforeEditAccepted?: (task: DHTMLXGanttItem) => boolean;
    additionalContent?: any;
}

function EditTaskDialog(props: EditTaskProps) {
    const { task, showDialog, setShowDialog, onTaskEdited, onBeforeEditAccepted, additionalContent } = props;

    const schema = z.object({
        text: z.string().min(1, { error: "Please supply a name for the task." }),
        start_date: z.date({ error: "Please select a start date." }),
        end_date: z.date({ error: "Please select an end date." })
    });
    type FormValues = z.infer<typeof schema>;

    const { handleSubmit, reset, control } = useForm({ resolver: zodResolver(schema) });

    React.useEffect(() => {
        reset(task);
    }, [task]);

    const onAcceptClicked: SubmitHandler<FormValues> = React.useCallback((formModel) => {
        const updatedTask = { ...task, ...formModel };
        if (onBeforeEditAccepted) {
            const shouldAcceptEdit = onBeforeEditAccepted(updatedTask);
            if (shouldAcceptEdit === false) return;
        }
        onTaskEdited(updatedTask);
        setShowDialog(false);
    }, [task, onTaskEdited, setShowDialog]);

    return (
        <Dialog onClose={() => setShowDialog(false)} open={showDialog}>
            <DialogTitleWithCloseButton onClose={() => setShowDialog(false)}>Edit Item</DialogTitleWithCloseButton>
            <form onSubmit={handleSubmit(onAcceptClicked)}>
                <DialogContent className="vertical-form padding-top-0">
                    <div className="vertical-form-field">Name</div>
                    <Controller
                        name="text"
                        control={control}
                        render={({
                            field,
                            fieldState: { error }
                        }) => (
                            <TextField
                                {...field}
                                error={!!error}
                                helperText={error?.message}
                                variant="outlined"
                                fullWidth
                            />
                        )}
                    />

                    <div className="vertical-form-field">Start Date</div>
                    <Controller
                        name="start_date"
                        control={control}
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <DatePicker
                                value={value as any}
                                onChange={(val => onChange(val?.toDate()))}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                    />

                    <div className="vertical-form-field">End Date</div>
                    <Controller
                        name="end_date"
                        control={control}
                        render={({
                            field: { value, onChange },
                            fieldState: { error }
                        }) => (
                            <DatePicker
                                value={value as any}
                                onChange={(val => onChange(val?.toDate()))}
                                error={!!error}
                                helperText={error?.message}
                            />
                        )}
                    />

                    {additionalContent}

                </DialogContent>
                <DialogActions>
                    <LinkButton color="secondary" onClick={() => setShowDialog(false)}>Cancel</LinkButton>
                    <Button type="submit" variant="contained" color="primary">Accept</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
}

export { EditTaskDialog };